import React, { useState, useEffect, useCallback } from "react";
import CloseIcon from "@mui/icons-material/Close";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import IconButton from "@mui/material/IconButton";
import { onEmailVerified } from "../lib/emailVerificationEvents";
import { AUTH_API_URL, authFetch } from "../lib/authFetch";

const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL || "support@drivenow.it.com";

async function resendVerificationEmail(token: string) {
  return authFetch(`${AUTH_API_URL}/resend-verification-pin`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
}

async function fetchMe(token: string) {
  return authFetch(`${AUTH_API_URL}/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

type Props = {
  /** Controls visibility; when false, dialog returns null. */
  open: boolean;
  /** JWT to authenticate the resend call. */
  token: string;
  /** Optional email to display in the subtitle for user confidence. */
  email?: string;
  /** When true, signup email dispatch failed — show support guidance immediately. */
  initialDeliveryFailed?: boolean;
  /** Called after the user verifies (magic link or profile refresh). */
  onVerified: () => void;
  /** Called when the user dismisses the dialog (X button or backdrop). */
  onClose: () => void;
  /** When true, applies the dark theme styling. */
  darkMode?: boolean;
};

/**
 * Modal shown after signup (or when a gated action is blocked) prompting the
 * user to verify via the magic link emailed by the auth-api. PIN entry was
 * removed from outbound mail for deliverability; this dialog matches that flow.
 */
const VerifyEmailDialog: React.FC<Props> = ({
  open,
  token,
  email,
  initialDeliveryFailed = false,
  onVerified,
  onClose,
  darkMode,
}) => {
  const [resendCooldown, setResendCooldown] = useState(30);
  const [statusMessage, setStatusMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [resendLoading, setResendLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setResendCooldown(30);
    setStatusMessage("");
    setErrorMessage(
      initialDeliveryFailed
        ? `We couldn't send a verification email right now. Use Resend link below or contact ${SUPPORT_EMAIL}.`
        : ""
    );
    setResendLoading(false);
  }, [open, initialDeliveryFailed]);

  useEffect(() => {
    if (!open || resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((prev) => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [open, resendCooldown]);

  // Magic-link landing page emits this when verification succeeds.
  useEffect(() => {
    if (!open) return undefined;
    return onEmailVerified(() => onVerified());
  }, [open, onVerified]);

  // If the user verifies in another tab, poll /me so this dialog closes here too.
  useEffect(() => {
    if (!open || !token) return undefined;
    let cancelled = false;

    const poll = async () => {
      try {
        const res = await fetchMe(token);
        if (!res.ok || cancelled) return;
        const me = await res.json().catch(() => null);
        if (me?.email_verified === true) onVerified();
      } catch {
        /* ignore transient network errors while polling */
      }
    };

    poll();
    const id = window.setInterval(poll, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [open, token, onVerified]);

  const handleResend = useCallback(async () => {
    if (resendCooldown > 0 || resendLoading) return;
    setResendLoading(true);
    setErrorMessage("");
    setStatusMessage("");

    try {
      const res = await resendVerificationEmail(token);
      const body = await res.json().catch(() => ({}));
      if (res.status === 429) {
        setErrorMessage(body.detail || "Too many emails requested. Please wait a few minutes.");
      } else if (!res.ok) {
        setErrorMessage(body.detail || "Failed to resend email. Please try again.");
      } else if (body.email_sent === false) {
        setErrorMessage(
          "We're having trouble sending email right now. Please try again shortly."
        );
      } else {
        setStatusMessage("A new verification link has been sent.");
        setResendCooldown(30);
      }
    } catch {
      setErrorMessage("Something went wrong. Please try again.");
    } finally {
      setResendLoading(false);
    }
  }, [resendCooldown, resendLoading, token]);

  if (!open) return null;

  const muted = darkMode ? "#b0b0b0" : "#555";

  return (
    <div
      className={`verify-email-dialog-backdrop Auth-form-container ${darkMode ? "dark" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="verify-email-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="verify-email-dialog-card">
        <IconButton
          onClick={onClose}
          size="small"
          className="verify-email-dialog-close"
          aria-label="Close verification dialog"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <div style={{ textAlign: "center", padding: "8px 4px" }}>
          <MailOutlineIcon
            sx={{ fontSize: 40, color: "var(--color-primary)", marginBottom: 1 }}
            aria-hidden
          />
          <h3 id="verify-email-title" className="Auth-form-title" style={{ marginBottom: 8 }}>
            Check your email
          </h3>
          <p style={{ color: muted, fontSize: 14, marginBottom: 8 }}>
            We sent a verification link to
          </p>
          <p style={{ color: "var(--color-primary)", fontWeight: 600, fontSize: 15, marginBottom: 16 }}>
            {email || "your email"}
          </p>
          <p style={{ color: muted, fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>
            Open the email and click <strong>Verify my email</strong> to finish setting up
            your account. The link expires in 10 minutes.
          </p>
          <p style={{ color: muted, fontSize: 13, lineHeight: 1.5, marginBottom: 20 }}>
            You can close this window and keep browsing — we&apos;ll unlock your account as
            soon as you click the link.
          </p>
          {statusMessage && (
            <p style={{ color: "#2e7d32", fontSize: 13, marginBottom: 12 }}>{statusMessage}</p>
          )}
          {errorMessage && (
            <p style={{ color: "#d32f2f", fontSize: 13, marginBottom: 12 }}>{errorMessage}</p>
          )}
          <button
            type="button"
            onClick={handleResend}
            disabled={resendCooldown > 0 || resendLoading}
            className="btn btn-outline-primary btn-sm"
          >
            {resendLoading
              ? "Sending..."
              : resendCooldown > 0
                ? `Resend link in ${resendCooldown}s`
                : "Resend link"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailDialog;
